import { create } from 'zustand';
import type { CampaignTypeKey, ScheduleModel, ZipValidationModel } from 'entities/campaign';
import type { ServiceType } from 'entities/service';

export const CAMPAIGN_INITIAL_NAME = '';

export type CreateCampaignStore = {
  campaignId: number;
  name: string;
  preSelectedType: CampaignTypeKey | undefined;
  type: CampaignTypeKey | undefined;
  zipCodes: string;
  serviceObject: ServiceType | undefined;
  bid: string | undefined;
  bidTiers: { bid: string; id: number }[] | undefined;
  schedule: ScheduleModel;
  dbaId?: number;
} & Partial<ZipValidationModel>;

interface Actions {
  actions: {
    resetStore: () => void;
    updateStepData: (data: Partial<CreateCampaignStore>) => void;
  };
}

const initialState: CreateCampaignStore = {
  campaignId: 0,
  dbaId: undefined,
  name: CAMPAIGN_INITIAL_NAME,
  preSelectedType: undefined,
  type: undefined,
  zipCodes: '',
  serviceObject: undefined,
  bid: undefined,
  bidTiers: undefined,
  schedule: {
    Monday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
    Tuesday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
    Wednesday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
    Thursday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
    Friday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
    Saturday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
    Sunday: {
      timeFrom: '12:00am',
      timeTo: '11:30pm',
      isSelected: false,
    },
  },
};

const useCreateCampaignStore = create<CreateCampaignStore & Actions>((set, get) => ({
  ...initialState,
  actions: {
    resetStore: () => set({ ...initialState }),
    updateStepData: (data) => {
      const newData = { ...data };
      const newCampaignType = data.type;
      const prevCampaignType = get().type;
      const newServiceObject = data.serviceObject;
      const prevServiceObject = get().serviceObject;

      // If user decided to change already selected campaign type, we should reset data for StepService and StepBid
      if (newCampaignType && newCampaignType !== prevCampaignType) {
        newData.bid = undefined;
        newData.bidTiers = prevServiceObject && getDefaultBidTiers(prevServiceObject);
      }

      if (newServiceObject && newServiceObject !== prevServiceObject) {
        newData.bid = undefined;
        newData.bidTiers = getDefaultBidTiers(newServiceObject);
        newData.type = undefined;
      }

      newData.bid && (newData.bidTiers = undefined);
      newData.bidTiers && (newData.bid = undefined);

      set(newData);
    },
  },
}));

const getDefaultBidTiers = (service: ServiceType) =>
  service.bidTierGroups[0]?.bidTiers.map(({ id }) => ({ id, bid: '' }));

export const useCampaignId = () => useCreateCampaignStore((state) => state.campaignId);
export const useCampaignName = () => useCreateCampaignStore((state) => state.name);
export const usePreSelectedType = () => useCreateCampaignStore((state) => state.preSelectedType);
export const useCampaignType = () => useCreateCampaignStore((state) => state.type);
export const useCampaignZipCodes = () => useCreateCampaignStore((state) => state.zipCodes);
export const useValidZipCount = () => useCreateCampaignStore((state) => state.validCount);
export const useDuplicateZipCount = () => useCreateCampaignStore((state) => state.duplicateCount);
export const useInvalidZipCount = () => useCreateCampaignStore((state) => state.invalidCount);
export const useServiceObject = () => useCreateCampaignStore((state) => state.serviceObject);
export const useBid = () => useCreateCampaignStore((state) => state.bid);
export const useBidTiers = () => useCreateCampaignStore((state) => state.bidTiers);
export const useSchedule = () => useCreateCampaignStore((state) => state.schedule);
export const useDbaId = () => useCreateCampaignStore((state) => state.dbaId);
export const useCreateCampaignStoreActions = () => useCreateCampaignStore((state) => state.actions);
